<script>
import i18n from "@/i18n";
import { required, email, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import axios from 'axios';
import Lottie from "@/components/widgets/lottie.vue";
import animationData from "@/components/widgets/Animation-1701822503345.json";
import Footer from "@/components/footer-login";
import Layout from "@/layouts/auth-layout.vue";
import PageHeader from "@/components/page-header";
import { authMethods, authFackMethods, notificationMethods } from "@/state/helpers";

export default {
    setup() {
        const v$ = useVuelidate();
        return {
            v$,
        };
    },
    components: {
        Lottie,
        Footer,
        Layout,
        PageHeader,
    },
    data() {
        return {
            email: "",
            password: "",
            submitted: false,
            authError: null,
            tryingToLogIn: false,
            isAuthError: false,
            processing: false,
            showSuccessModal: false,
            defaultOptions: { animationData: animationData },
            passwordVisible: false,
        };
    },
    validations() {
        return {
            email: {
                required: helpers.withMessage(() => this.$t('validation-email-required'), required),
                email: helpers.withMessage(() => this.$t('validation-email-format'), email),
            },
            password: {
                required: helpers.withMessage(() => this.$t('validation-password-required'), required),
            },
        };
    },
    methods: {
        ...authMethods,
        ...authFackMethods,
        ...notificationMethods,

        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible;
        },
        async tryToLogIn() {
            this.submitted = true;
            this.v$.$touch();  // Trigger Vuelidate's validation

            if (this.v$.$invalid) {
                return;
            }

            this.processing = true;
            try {
                const response = await axios.post(`${process.env.VUE_APP_BACKEND_URL}/api/login/`, {
                    email: this.email,
                    password: this.password,
                });
                if (response.data.message) {
                    i18n.global.locale.value = response.data.locale;
                    this.showSuccessModal = true;
                }
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    // Handle lockout case
                    this.authError = this.$t('account-bloccato');
                } else {
                    this.authError = this.$t('errore-credenziali-valide');
                }
                this.isAuthError = true;
            } finally {
                this.processing = false;
            }
        },
        closeSuccessModalAndRedirect() {
            this.showSuccessModal = false;
            this.$router.push({
                name: 'twostep-basic',
                query: { email: this.email }
            });
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader titoloScheda="Login" />
        <BRow class="justify-content-center">
            <BCol md="8" lg="6" xl="5">
                <BCard no-body class="mt-4">
                    <BCardBody class="p-4">
                        <div class="text-center mt-2">
                            <h5 class="text-primary">{{ $t('bentornato') }}</h5>
                            <p class="text-muted">{{ $t('effettua-il-login-per-continuare-su') }} {{ $appConfig.nomeSito }}</p>
                        </div>
                        <div class="p-2 mt-4">
                            <b-alert v-model="isAuthError" variant="danger" class="mb-3" dismissible>{{ authError }}</b-alert>
                            <form @submit.prevent="tryToLogIn">
                                <div class="mb-3">
                                    <label for="email" class="form-label">Email</label>
                                    <input type="email" class="form-control" id="email" :class="{'is-invalid': submitted && v$.email.$error}" :placeholder="$t('placeholder-inserisci-email')" v-model="email" /> 
                                    <div v-if="submitted && v$.email.$error" class="invalid-feedback">
                                        <div v-for="error in v$.email.$errors" :key="error.$uid">
                                            <span>{{ error.$message }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div class="mb-3">
                                    <div class="float-end">
                                        <router-link to="/forgot-password" class="text-muted">{{ $t('password-dimenticata') }}</router-link>
                                    </div>
                                    <label class="form-label" for="password-input">Password</label>
                                    <div class="position-relative auth-pass-inputgroup mb-3">
                                        <input :type="passwordVisible ? 'text' : 'password'" v-model="password" class="form-control pe-5" :class="{'is-invalid': submitted && v$.password.$error}" :placeholder="$t('placeholder-inserisci-password')" id="password-input" />
                                        <BButton variant="link" class="position-absolute end-0 top-0 text-decoration-none text-muted" type="button" id="password-addon" @click="togglePasswordVisibility" >
                                            <i class="ri-eye-fill align-middle"></i>
                                        </BButton>
                                        <div v-if="submitted && v$.password.$error" class="invalid-feedback">
                                            <div v-for="error in v$.password.$errors" :key="error.$uid">
                                                <span>{{ error.$message }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-4">
                                    <b-button variant="warning" class="w-100" type="submit" :disabled="processing"> {{ processing ? $t('attendere') + "..." : "Login" }} </b-button>
                                </div>
                            </form>

                        </div>
                    </BCardBody>
                </BCard>

                <div class="mt-4 text-center">
                    <p class="mb-0"> {{ $t('non-hai-un-account') }}
                        <router-link to="/register" class="fw-semibold text-primary text-decoration-underline">{{ $t('registrati') }} </router-link>
                    </p>
                </div>
            </BCol>
        </BRow>

        <b-modal v-model="showSuccessModal" hide-footer hide-header-close no-close-on-backdrop no-close-on-esc class="v-modal-custom" modal-class="zoomIn" centered>
            <lottie colors="primary:#121331,secondary:#08a88a" trigger="loop" :options="defaultOptions" :height="120" :width="120" />
            <div class="text-center">
                <div class="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                    <h4>{{ $t('credenziali-corrette') }}</h4>
                    <p class="text-muted mx-4 mb-4">{{ $t('per-la-tua-sicurezza-ti-abbiamo-inviato-una-mail-con-un-codice') }}</p>
                </div>
            </div>
            <div class="text-center">
                <b-button variant="warning" @click="closeSuccessModalAndRedirect">{{ $t('inserisci-codice') }}</b-button>
            </div>
        </b-modal>
    </Layout>
    <Footer />
</template>